import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SwaggerAuthService } from '../../services/auth.service';

/**
 * Swagger Dashboard Component
 * 
 * Component displaying available tools after successful authentication.
 * Acts as a middle page between login and final destinations.
 * 
 * Features:
 * - Display authenticated user information
 * - Navigation to available tools (Swagger UI, Hangfire)
 * - Logout functionality
 */
@Component({
    selector: 'swagger-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss']
})
export class SwaggerDashboardComponent implements OnInit {
    private readonly router: Router;
    private readonly authService: SwaggerAuthService;

    username: string = '';
    swaggerUrl: string = '';
    hangfireUrl: string = '';
    private encryptedToken: string | null = null;

    constructor(
        router: Router,
        authService: SwaggerAuthService
    ) {
        this.router = router;
        this.authService = authService;

        // Get encrypted token from router state
        const navigation = this.router.getCurrentNavigation();
        this.encryptedToken = navigation?.extras?.state?.['encryptedToken'] || null;
    }

    ngOnInit(): void {
        if (!this.authService.isAuthenticated()) {
            this.router.navigate(['/ui/login']);
            return;
        }

        if (!this.encryptedToken) {
            this.encryptedToken = this.authService.getToken();
        }

        this.authService.currentUser$.subscribe(user => {
            if (user) {
                this.username = user.username || user.displayName || '';
            }
        });

        const settings = this.authService.getSettings();
        this.swaggerUrl = settings.swaggerUrl || '';
        this.hangfireUrl = settings.hangfireUrl || '';
    }

    /**
     * Navigate to Swagger UI with encrypted token
     * Redirects to backend endpoint which decrypts token and grants Swagger access
     */
    navigateToSwagger(): void {
        if (!this.encryptedToken) {
            this.router.navigate(['/ui/login']);
            return;
        }

        const settings = this.authService.getSettings();
        const apiUrl = settings.apiUrl || '';
        const swaggerRedirectUrl = `${apiUrl}/auth/swagger-redirect?token=${encodeURIComponent(this.encryptedToken)}`;
        window.location.href = swaggerRedirectUrl;
    }

    navigateToHangfire(): void {
    }

    /**
     * Logout user and redirect to login page
     */
    logout(): void {
        this.authService.logout();
        this.router.navigate(['/ui/login']);
    }
}
